/**
 * The page every refused LTI launch ends on (TECH-3186).
 *
 * All three legs redirect here with `?code=`; `lib/lti/error-copy.ts` owns what
 * each one says. The code is shown at the bottom on purpose. The reader can
 * rarely act on any of this, so the useful thing the page can do is give them
 * something exact to quote to whoever can.
 */
import { createFileRoute } from "@tanstack/react-router";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ltiErrorCopy } from "@/lib/lti/error-copy";

type LtiErrorSearch = {
	code?: string;
};

export const Route = createFileRoute("/lti/error")({
	// Untrusted. This arrives from an address bar as readily as from a redirect.
	// `ltiErrorCopy` treats an unknown code as unknown rather than as a default.
	validateSearch: (search: Record<string, unknown>): LtiErrorSearch => ({
		code: typeof search.code === "string" ? search.code : undefined,
	}),
	component: LtiErrorComponent,
});

function LtiErrorComponent() {
	const { code } = Route.useSearch();
	const { title, body, retry } = ltiErrorCopy(code);

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					<CardDescription>{body}</CardDescription>
				</CardHeader>
				{code && (
					<CardContent>
						<p className="text-muted-foreground text-xs">
							If you need to report this, the reason is{" "}
							<code className="font-mono">{code}</code>.
						</p>
					</CardContent>
				)}
				{retry && (
					<CardFooter>
						{/* No button that retries from here: the launch has to start at
						    the platform, and this tab has nothing left to replay. */}
						<p className="text-muted-foreground text-sm">
							Go back to your course and open ELASO again.
						</p>
					</CardFooter>
				)}
			</Card>
		</div>
	);
}
